import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Table, Tag, Select, DatePicker, Button } from "antd";
import { FiDownload, FiFilter, FiCalendar, FiFileText, FiBarChart2, FiCheckCircle, FiTrendingUp } from "react-icons/fi";

export default function ReportsPage() {
	const [selectedReport, setSelectedReport] = useState(null);
	const [dateRange, setDateRange] = useState(null);

	// Reports Data
	const reportsData = [
		{
			id: "RPT-001",
			name: "Monthly Security Summary",
			type: "Security",
			description: "Comprehensive monthly overview of security incidents and threats",
			lastGenerated: "2025-01-02 14:30:00",
			frequency: "Monthly",
			status: "Ready",
			size: "2.4 MB",
			format: "PDF",
		},
		{
			id: "RPT-002",
			name: "Vulnerability Assessment Report",
			type: "Vulnerability",
			description: "Detailed vulnerability scan results with remediation recommendations",
			lastGenerated: "2025-01-01 10:15:00",
			frequency: "Weekly",
			status: "Ready",
			size: "1.8 MB",
			format: "PDF",
		},
		{
			id: "RPT-003",
			name: "Asset Inventory Report",
			type: "Asset",
			description: "Complete inventory of all OT infrastructure assets and devices",
			lastGenerated: "2024-12-31 09:00:00",
			frequency: "Monthly",
			status: "Ready",
			size: "3.2 MB",
			format: "Excel",
		},
		{
			id: "RPT-004",
			name: "Compliance Status Report",
			type: "Compliance",
			description: "IEC 62443 and NIST compliance status with audit trail",
			lastGenerated: "2024-12-30 16:45:00",
			frequency: "Quarterly",
			status: "Ready",
			size: "2.9 MB",
			format: "PDF",
		},
		{
			id: "RPT-005",
			name: "Network Traffic Analysis",
			type: "Network",
			description: "Detailed network traffic patterns and anomaly detection results",
			lastGenerated: "2025-01-02 12:30:00",
			frequency: "Daily",
			status: "Ready",
			size: "1.5 MB",
			format: "PDF",
		},
		{
			id: "RPT-006",
			name: "Incident Response Timeline",
			type: "Incident",
			description: "Timeline of detected incidents with response actions taken",
			lastGenerated: "2025-01-02 08:00:00",
			frequency: "Weekly",
			status: "Ready",
			size: "2.1 MB",
			format: "Excel",
		},
		{
			id: "RPT-007",
			name: "System Performance Report",
			type: "Performance",
			description: "OT system performance metrics and availability analysis",
			lastGenerated: "2024-12-28 11:20:00",
			frequency: "Bi-weekly",
			status: "Ready",
			size: "1.7 MB",
			format: "PDF",
		},
		{
			id: "RPT-008",
			name: "Access Control Audit",
			type: "Access",
			description: "User access permissions and privilege audit report",
			lastGenerated: "2024-12-25 15:00:00",
			frequency: "Monthly",
			status: "Ready",
			size: "1.3 MB",
			format: "PDF",
		},
	];

	// Report generation history
	const generationHistory = [
		{
			id: "GEN-001",
			reportName: "Monthly Security Summary",
			generatedDate: "2025-01-02 14:30:00",
			generatedBy: "System Admin",
			format: "PDF",
			status: "Completed",
			size: "2.4 MB",
		},
		{
			id: "GEN-002",
			reportName: "Vulnerability Assessment Report",
			generatedDate: "2025-01-01 10:15:00",
			generatedBy: "Security Team",
			format: "PDF",
			status: "Completed",
			size: "1.8 MB",
		},
		{
			id: "GEN-003",
			reportName: "Asset Inventory Report",
			generatedDate: "2024-12-31 09:00:00",
			generatedBy: "Operations",
			format: "Excel",
			status: "Completed",
			size: "3.2 MB",
		},
		{
			id: "GEN-004",
			reportName: "Network Traffic Analysis",
			generatedDate: "2024-12-30 16:45:00",
			generatedBy: "Network Team",
			format: "PDF",
			status: "Completed",
			size: "1.5 MB",
		},
		{
			id: "GEN-005",
			reportName: "Compliance Status Report",
			generatedDate: "2024-12-29 14:20:00",
			generatedBy: "Compliance Officer",
			format: "PDF",
			status: "Completed",
			size: "2.9 MB",
		},
	];

	// Helper functions
	const getTypeColor = (type) => {
		const colors = {
			Security: "#3b82f6",
			Vulnerability: "#ef4444",
			Asset: "#f59e0b",
			Compliance: "#10b981",
			Network: "#8b5cf6",
			Incident: "#f97316",
			Performance: "#06b6d4",
			Access: "#ec4899",
		};
		return colors[type] || "#6b7280";
	};

	// Reports table columns
	const reportsColumns = [
		{
			title: "Report ID",
			dataIndex: "id",
			key: "id",
			render: (text) => <span className="fw-bold text-gray-900">{text}</span>,
			width: 100,
		},
		{
			title: "Report Name",
			dataIndex: "name",
			key: "name",
			render: (text) => <span className="fw-semibold text-gray-900">{text}</span>,
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			render: (type) => (
				<Tag color={getTypeColor(type)} style={{ borderRadius: "4px", fontWeight: "600" }}>
					{type}
				</Tag>
			),
			width: 110,
		},
		{
			title: "Frequency",
			dataIndex: "frequency",
			key: "frequency",
			render: (freq) => <span className="text-gray-700">{freq}</span>,
			width: 100,
		},
		{
			title: "Last Generated",
			dataIndex: "lastGenerated",
			key: "lastGenerated",
			width: 160,
		},
		{
			title: "Size",
			dataIndex: "size",
			key: "size",
			render: (size) => <span className="fw-semibold">{size}</span>,
			width: 80,
		},
		{
			title: "Format",
			dataIndex: "format",
			key: "format",
			render: (format) => <Tag style={{ borderRadius: "4px", background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" }}>{format}</Tag>,
			width: 80,
		},
		{
			title: "Action",
			key: "action",
			render: (_, record) => (
				<button type="button" className="btn btn-sm btn-primary fw-bold">
					Download
				</button>
			),
			width: 100,
		},
	];

	// Generation history table columns
	const historyColumns = [
		{
			title: "Generation ID",
			dataIndex: "id",
			key: "id",
			render: (text) => <span className="fw-bold text-gray-900 fs-6">{text}</span>,
			width: 110,
		},
		{
			title: "Report Name",
			dataIndex: "reportName",
			key: "reportName",
			render: (text) => <span className="fw-semibold text-gray-900">{text}</span>,
		},
		{
			title: "Generated Date",
			dataIndex: "generatedDate",
			key: "generatedDate",
			width: 160,
		},
		{
			title: "Generated By",
			dataIndex: "generatedBy",
			key: "generatedBy",
			width: 120,
		},
		{
			title: "Format",
			dataIndex: "format",
			key: "format",
			render: (format) => <Tag style={{ borderRadius: "4px", background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" }}>{format}</Tag>,
			width: 80,
		},
		{
			title: "Size",
			dataIndex: "size",
			key: "size",
			render: (size) => <span className="fw-semibold">{size}</span>,
			width: 100,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<Tag color="#10b981" style={{ borderRadius: "4px", fontWeight: "600" }}>
					{status}
				</Tag>
			),
			width: 100,
		},
		{
			title: "Action",
			key: "action",
			render: (_, record) => (
				<button type="button" className="btn btn-sm btn-primary fw-bold">
					View
				</button>
			),
			width: 80,
		},
	];

	return (
		<>
			<Helmet>
				<title>Reports | Sec1 Software Pvt. Ltd.</title>
			</Helmet>

			<div id="kt_app_toolbar" className="app-toolbar pt-12 rounded-0">
				<div id="kt_app_toolbar_container" className="app-container container-fluid d-flex align-items-stretch">
					<div className="app-toolbar-wrapper d-flex flex-stack flex-wrap gap-4 w-100">
						<div className="page-title d-flex flex-column gap-1 me-3 mb-2">
							<h1 className="page-heading d-flex flex-column justify-content-center text-dark fw-bolder fs-1 lh-0">
								Reports & Analytics
							</h1>
						</div>
					</div>
				</div>
			</div>

			<div id="kt_app_content" className="app-content flex-column-fluid">
				<div id="kt_app_content_container" className="app-container container-fluid">
					{/* Statistics Cards */}
					<div className="mb-3">
						<div className="row g-2">
							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiFileText className="fs-2hx text-primary" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">8</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Total Reports</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiCheckCircle className="fs-2hx text-success" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">8</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Ready to Download</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiTrendingUp className="fs-2hx text-info" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">5</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Generated This Month</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiBarChart2 className="fs-2hx text-warning" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">12.5</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Total Size (MB)</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Available Reports Section */}
					<div className="card mb-3">
						<div className="card-header border-0 pt-5">
							<h3 className="card-title align-items-start flex-column">
								<span className="card-label fw-bold text-dark">Available Reports</span>
								<span className="text-muted mt-1 fw-semibold fs-7">OT security and compliance reports</span>
							</h3>
							<div className="card-toolbar">
								<button type="button" className="btn btn-sm btn-primary fw-bold d-flex align-items-center gap-2">
									<FiDownload size={16} />
									Generate New Report
								</button>
							</div>
						</div>
						<div className="card-body">
							<Table
								dataSource={reportsData}
								columns={reportsColumns}
								pagination={{ pageSize: 10, total: reportsData.length }}
								scroll={{ x: 1200 }}
								className="antd-override"
								rowKey={(record) => record.id}
							/>
						</div>
					</div>

					{/* Generation History Section */}
					<div className="card">
						<div className="card-header border-0 pt-5">
							<h3 className="card-title align-items-start flex-column">
								<span className="card-label fw-bold text-dark">Report Generation History</span>
								<span className="text-muted mt-1 fw-semibold fs-7">Recently generated reports archive</span>
							</h3>
							<div className="card-toolbar">
								<button type="button" className="btn btn-sm btn-outline-secondary fw-bold d-flex align-items-center gap-2">
									<FiFilter size={16} />
									Filter
								</button>
							</div>
						</div>
						<div className="card-body">
							<Table
								dataSource={generationHistory}
								columns={historyColumns}
								pagination={{ pageSize: 10, total: generationHistory.length }}
								scroll={{ x: 1200 }}
								className="antd-override"
								rowKey={(record) => record.id}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
